import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { usePoints } from './usePoints';
import type { User } from '../types/user';
import type { Pet } from '../types/pets';
import type { Item } from '../types/items';

export function usePurchase(user: User | null) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { updatePoints } = usePoints();

  const checkPetOwnership = async (walletAddress: string, petId: string) => {
    const { data, error } = await supabase
      .from('user_pets')
      .select('id')
      .eq('wallet_address', walletAddress)
      .eq('pet_id', petId);

    if (error) throw error;
    return data && data.length > 0;
  };

  const purchasePet = async (pet: Pet) => {
    if (!user) {
      setError('Please log in to make a purchase');
      return false;
    }

    if (user.points < pet.basePrice) {
      setError('Insufficient funds');
      return false;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Check if user already owns this pet
      const alreadyOwned = await checkPetOwnership(user.walletAddress, pet.id);
      if (alreadyOwned) {
        setError('You already own this pet!');
        return false;
      }

      // Start transaction by deducting points
      const { error: pointsError } = await supabase
        .from('leaderboard')
        .update({ points: user.points - pet.basePrice })
        .eq('wallet_address', user.walletAddress);

      if (pointsError) throw pointsError;

      // Generate a unique name with random number
      const uniqueName = `${pet.name} #${Math.floor(Math.random() * 9000) + 1000}`;

      // Insert the pet
      const { error: purchaseError } = await supabase
        .from('user_pets')
        .insert({
          wallet_address: user.walletAddress,
          pet_id: pet.id,
          name: uniqueName,
          level: 1,
          experience: 0,
          equipped_items: []
        });

      if (purchaseError) {
        // Rollback points if pet insert fails
        await supabase
          .from('leaderboard')
          .update({ points: user.points })
          .eq('wallet_address', user.walletAddress);
        throw purchaseError;
      }

      // Update local user state
      await updatePoints(user, -pet.basePrice);
      return true;
    } catch (err) {
      console.error('Purchase failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete purchase');
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const checkItemOwnership = async (walletAddress: string, itemId: string) => {
    const { data, error } = await supabase
      .from('user_items')
      .select('quantity')
      .eq('wallet_address', walletAddress)
      .eq('item_id', itemId);

    if (error) throw error;
    return data && data.length > 0 ? data[0] : null;
  };

  const purchaseItem = async (item: Item) => {
    if (!user) {
      setError('Please log in to make a purchase');
      return false;
    }

    if (user.points < item.price) {
      setError('Insufficient funds');
      return false;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Check if user already owns this item
      const existingItem = await checkItemOwnership(user.walletAddress, item.id);

      // Start transaction by deducting points
      const { error: pointsError } = await supabase
        .from('leaderboard')
        .update({ points: user.points - item.price })
        .eq('wallet_address', user.walletAddress);

      if (pointsError) throw pointsError;

      if (existingItem) {
        // Update quantity if item already owned
        const { error: updateError } = await supabase
          .from('user_items')
          .update({ quantity: existingItem.quantity + 1 })
          .eq('wallet_address', user.walletAddress)
          .eq('item_id', item.id);

        if (updateError) throw updateError;
      } else {
        // Insert new item if not owned
        const { error: insertError } = await supabase
          .from('user_items')
          .insert({
            wallet_address: user.walletAddress,
            item_id: item.id,
            quantity: 1
          });

        if (insertError) throw insertError;
      }

      // Update local user state
      await updatePoints(user, -item.price);
      return true;
    } catch (err) {
      console.error('Purchase failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete purchase');
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    purchasePet,
    purchaseItem,
    isProcessing,
    error,
    clearError: () => setError(null)
  };
}